import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  AuthUser,
  CreateUserForm,
  UpdateUserForm,
  ApiResponse,
  PaginationParams,
  PaginatedResponse,
  DashboardStats,
  RevenueData,
  Booking,
  Route
} from '../types';
import { EmailService } from './emailService';

export class ApiService {
  // User Management
  static async getUsers(params?: PaginationParams): Promise<PaginatedResponse<AuthUser>> {
    try {
      const usersRef = collection(db, 'users');
      const pageLimit = params?.limit || 50;
      let q: any = query(usersRef, orderBy('createdAt', 'desc'), limit(pageLimit));
      if (params?.startAfterDate) {
        q = query(usersRef, orderBy('createdAt', 'desc'), startAfter(params.startAfterDate), limit(pageLimit));
      }

      const snapshot = await getDocs(q);
      const users = snapshot.docs.map(doc => {
        const data = doc.data() as any;
        return {
          id: doc.id,
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phoneNumber: data.phoneNumber || '',
          role: data.role || 'user',
          isActive: data.isActive !== undefined ? data.isActive : true,
          isEmailVerified: data.isEmailVerified !== undefined ? data.isEmailVerified : false,
          isPhoneVerified: data.isPhoneVerified !== undefined ? data.isPhoneVerified : false,
          notificationPrefs: data.notificationPrefs || {
            email: true,
            sms: false,
            push: true,
          },
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          firebaseId: data.firebaseId,
          profileImageUrl: data.profileImageUrl,
        } as AuthUser;
      });

      return {
        data: users,
        pagination: {
          page: params?.page || 1,
          limit: pageLimit,
          total: users.length,
          totalPages: Math.ceil(users.length / pageLimit),
          hasNext: users.length === pageLimit,
          hasPrev: false,
        }
      };
    } catch (error: any) {
      throw new Error(`Failed to fetch users: ${error.message}`);
    }
  }

  static async getUserById(id: string): Promise<AuthUser | null> {
    try {
      const userRef = doc(db, 'users', id);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        return {
          id: userSnap.id,
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phoneNumber: data.phoneNumber || '',
          role: data.role || 'user',
          isActive: data.isActive !== undefined ? data.isActive : true,
          isEmailVerified: data.isEmailVerified !== undefined ? data.isEmailVerified : false,
          isPhoneVerified: data.isPhoneVerified !== undefined ? data.isPhoneVerified : false,
          notificationPrefs: data.notificationPrefs || {
            email: true,
            sms: false,
            push: true,
          },
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          firebaseId: data.firebaseId,
          profileImageUrl: data.profileImageUrl,
        } as AuthUser;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Failed to fetch user: ${error.message}`);
    }
  }

  static async createUser(userData: CreateUserForm): Promise<ApiResponse<AuthUser>> {
    try {
      // Validate input data
      if (!userData.email || !userData.password || !userData.firstName || !userData.lastName || !userData.phoneNumber) {
        return {
          success: false,
          error: 'All required fields must be provided',
        };
      }

      // Import AuthService here to avoid circular dependency
      const { AuthService } = await import('./auth');

      // Use AuthService to create user with Firebase Auth
      const result = await AuthService.createAdminUser(
        userData.email,
        userData.password,
        userData.firstName,
        userData.lastName,
        userData.phoneNumber,
        userData.role
      );

      if (result.success && result.user) {
        return {
          success: true,
          data: result.user,
          message: 'User created successfully',
        };
      } else {
        return {
          success: false,
          error: result.error || 'Failed to create user',
        };
      }
    } catch (error: any) {
      console.error('Error creating user:', error);

      // Handle specific Firebase errors
      if (error.code === 'auth/email-already-in-use') {
        return {
          success: false,
          error: 'Email address is already in use',
        };
      } else if (error.code === 'auth/weak-password') {
        return {
          success: false,
          error: 'Password is too weak. Please choose a stronger password',
        };
      } else if (error.code === 'auth/invalid-email') {
        return {
          success: false,
          error: 'Invalid email address format',
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to create user',
      };
    }
  }

  static async updateUser(id: string, userData: UpdateUserForm): Promise<ApiResponse<AuthUser>> {
    try {
      const userRef = doc(db, 'users', id);
      const updateData = {
        ...userData,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(userRef, updateData);

      const updatedUser = await this.getUserById(id);
      return {
        success: true,
        data: updatedUser!,
        message: 'User updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update user',
      };
    }
  }

  static async deleteUser(id: string): Promise<ApiResponse<void>> {
    try {
      const userRef = doc(db, 'users', id);
      await deleteDoc(userRef);

      return {
        success: true,
        message: 'User deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to delete user',
      };
    }
  }

  // Booking Management
  static async getBookings(params?: PaginationParams & { startAfterDate?: Date }): Promise<PaginatedResponse<any>> {
    try {
      const bookingsRef = collection(db, 'bookings');
      const pageLimit = params?.limit || 50;
      let q: any = query(bookingsRef, orderBy('createdAt', 'desc'), limit(pageLimit));
      if (params?.startAfterDate) {
        q = query(q, startAfter(params.startAfterDate));
      }

      const snapshot = await getDocs(q);
      const bookings = snapshot.docs.map(doc => {
        const data = doc.data() as any;
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          bookingDate: data.bookingDate?.toDate() || new Date(),
          travelDate: data.travelDate?.toDate() || new Date(),
        };
      });

      return {
        data: bookings,
        pagination: {
          page: params?.page || 1,
          limit: pageLimit,
          total: bookings.length,
          totalPages: Math.ceil(bookings.length / pageLimit),
          hasNext: bookings.length === pageLimit,
          hasPrev: false
        }
      };
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  static async updateBooking(bookingId: string, data: any): Promise<ApiResponse<any>> {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        ...data,
        updatedAt: Timestamp.now()
      });

      return {
        success: true,
        data: { id: bookingId, ...data }
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  static async deleteBooking(bookingId: string): Promise<ApiResponse<void>> {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await deleteDoc(bookingRef);

      return {
        success: true
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Dashboard Stats
  static async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    try {
      const [usersSnapshot, bookingsSnapshot, routesSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'bookings')),
        getDocs(collection(db, 'routes')),
      ]);

      const users = usersSnapshot.docs.length;
      const bookings = bookingsSnapshot.docs.length;
      const routes = routesSnapshot.docs.length;

      // Calculate revenue
      let totalRevenue = 0;
      bookingsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.paymentStatus === 'paid') {
          totalRevenue += data.totalAmount || 0;
        }
      });

      // Calculate booking statuses
      let activeBookings = 0;
      let completedBookings = 0;
      let cancelledBookings = 0;
      let pendingBookings = 0;

      bookingsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        switch (data.bookingStatus) {
          case 'confirmed':
            activeBookings++;
            break;
          case 'completed':
            completedBookings++;
            break;
          case 'cancelled':
            cancelledBookings++;
            break;
          case 'pending':
            pendingBookings++;
            break;
        }
      });

      const stats: DashboardStats = {
        totalUsers: users,
        totalBookings: bookings,
        totalRoutes: routes,
        totalRevenue,
        activeBookings,
        completedBookings,
        cancelledBookings,
        pendingBookings,
        userGrowth: 0, // TODO: Calculate growth
        bookingGrowth: 0, // TODO: Calculate growth
        revenueGrowth: 0, // TODO: Calculate growth
      };

      return {
        success: true,
        data: stats,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch dashboard stats',
      };
    }
  }

  // Revenue Data
  static async getRevenueData(dateRange: string): Promise<ApiResponse<RevenueData[]>> {
    try {
      const bookingsRef = collection(db, 'bookings');
      const q = query(bookingsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const bookings = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          totalPrice: data.totalPrice || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
        };
      });

      // Calculate revenue data based on date range
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const revenueData: RevenueData[] = [];

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);

        const dayBookings = bookings.filter(booking => {
          const bookingDate = booking.createdAt;
          return bookingDate.getDate() === date.getDate() &&
            bookingDate.getMonth() === date.getMonth() &&
            bookingDate.getFullYear() === date.getFullYear();
        });

        const revenue = dayBookings.reduce((sum, booking) => sum + booking.totalPrice, 0);
        const bookingCount = dayBookings.length;

        revenueData.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          revenue: revenue,
          bookings: bookingCount,
        });
      }

      return {
        success: true,
        data: revenueData,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch revenue data',
      };
    }
  }

  // Create booking with email notifications
  static async createBooking(bookingData: any): Promise<ApiResponse<Booking>> {
    try {
      // Create booking in Firestore
      const bookingRef = await addDoc(collection(db, 'bookings'), {
        ...bookingData,
        createdAt: Timestamp.now(),
        status: 'confirmed',
        paymentStatus: 'pending'
      });

      const booking: Booking = {
        id: bookingRef.id,
        ...bookingData,
        createdAt: new Date()
      };

      // Get route details for email
      const routeRef = doc(db, 'routes', bookingData.routeId);
      const routeSnap = await getDoc(routeRef);

      if (routeSnap.exists()) {
        const routeData = routeSnap.data();
        const route: Route = {
          id: routeSnap.id,
          fromCity: routeData.fromCity || '',
          toCity: routeData.toCity || '',
          price: routeData.price || 0,
          distance: routeData.distance || 0,
          duration: routeData.duration || 0,
          schedule: routeData.schedule || { isRecurring: false, days: [] },
          stops: routeData.stops || [],
          rates: routeData.rates || { luggage: 0, bag: 0 },
          isActive: routeData.isActive ?? true,
          departureTime: routeData.departureTime || '',
          arrivalTime: routeData.arrivalTime || '',
          createdAt: routeData.createdAt?.toDate() || new Date(),
          updatedAt: routeData.updatedAt?.toDate() || new Date()
        };

        // Send booking confirmation email
        await EmailService.sendBookingConfirmation(booking, route);

        // Send admin notification email
        await EmailService.sendAdminNotification(booking, route);

        // Schedule travel reminder email (24 hours before departure)
        const [depHour, depMin] = route.departureTime.split(':').map(Number);
        const reminderTime = new Date(booking.travelDate);
        reminderTime.setHours(depHour, depMin, 0, 0);
        reminderTime.setHours(reminderTime.getHours() - 24);

        if (reminderTime > new Date()) {
          await EmailService.scheduleEmail({
            to: booking.passengerEmail,
            toName: booking.passengerName,
            template: 'travelReminder',
            data: {
              route: `${route.fromCity} → ${route.toCity}`,
              departureTime: route.departureTime,
              seatNumber: booking.seatNumber,
              bookingReference: booking.bookingReference
            },
            priority: 'normal'
          }, reminderTime);
        }

        // Schedule feedback request email (after travel date)
        // Assuming arrival time is on the same day for simplicity, or we could calculate based on duration
        const [arrHour, arrMin] = route.arrivalTime.split(':').map(Number);
        const feedbackTime = new Date(booking.travelDate);
        feedbackTime.setHours(arrHour, arrMin, 0, 0);
        // If arrival time is earlier than departure, assume next day (simple heuristic)
        if (arrHour < depHour) {
          feedbackTime.setDate(feedbackTime.getDate() + 1);
        }
        feedbackTime.setHours(feedbackTime.getHours() + 2); // 2 hours after arrival

        await EmailService.scheduleEmail({
          to: booking.passengerEmail,
          toName: booking.passengerName,
          template: 'feedbackRequest',
          data: {
            route: `${route.fromCity} → ${route.toCity}`,
            travelDate: booking.travelDate, // Direct string access
            departureTime: route.departureTime, // Added for consistency or potential use
            seatNumber: booking.seatNumber,
            feedbackUrl: `https://wote-intercity.web.app/feedback?booking=${booking.id}`
          },
          priority: 'low'
        }, feedbackTime);
      }

      return {
        success: true,
        data: booking
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create booking',
      };
    }
  }

  // Get email logs for admin dashboard
  static async getEmailLogs(): Promise<ApiResponse<any[]>> {
    try {
      const emailLogs = await EmailService.getEmailLogs(100);
      return {
        success: true,
        data: emailLogs
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch email logs',
      };
    }
  }
}
